import { cloneDeep, get, set } from 'lodash';

import { type Answers } from 'types/zod/answers';
import { type GiftTrustItem } from 'types/zod/gift-trust-item';
import { type QuestionnaireInstance } from 'types/zod/questionnaire-instance';

type QInstanceId = QuestionnaireInstance['questionnaire_instance_id'];

const GiftTrustService = {
    getGiftTrustItems: (answers: Answers): GiftTrustItem[] => {
        const gifts = answers?.hidden?.gifts?._loop || [];
        return gifts
            .map((gift: any) => gift?.gift)
            .map((gift: any, index: number): GiftTrustItem => {
                return {
                    id: index,
                    date: gift.date,
                    value: gift.value,
                    description: gift.description,
                    withdrawLetterGenerated: !!gift.document_id,
                    documentId: gift.document_id,
                };
            });
    },

    getGiftTrustItemsForAllQInstances: (
        allAnswers: Record<QInstanceId, Answers[]>,
    ): Record<QInstanceId, GiftTrustItem[]> => {
        const qInstanceIds = Object.keys(allAnswers) as unknown as QInstanceId[];
        const giftTrustItems = qInstanceIds.reduce((acc, qInstanceId) => {
            const answers = allAnswers[qInstanceId];
            const giftTrustItems = GiftTrustService.getGiftTrustItems(answers);
            return { ...acc, [qInstanceId]: giftTrustItems };
        }, {} as Record<QInstanceId, GiftTrustItem[]>);
        return giftTrustItems;
    },

    removeGiftTrustItem: (params: { answers: Answers; giftTrustItemId: GiftTrustItem['id'] }): Answers => {
        const { answers, giftTrustItemId } = params;
        const newAnswers = cloneDeep(answers);
        newAnswers.hidden.gifts._loop.splice(giftTrustItemId, 1);
        return newAnswers;
    },

    getIsSigned: (answers: Answers): boolean => {
        return get(answers, 'hidden.finalization.signed', false);
    },

    setIsSigned: (answers: Answers): Answers => {
        const newAnswers = cloneDeep(answers);
        set(newAnswers, 'hidden.finalization.signed', true);
        return newAnswers;
    },

    getGiftTrustName: (answers: Answers): string => {
        return get(answers, 'trust_name', '');
    },
};

export default GiftTrustService;
